import React from 'react';
import { normalizarTexto } from '../../utils/texto';
import { motion } from 'framer-motion';
import {
    Clock,
    CheckCircle,
    XCircle,
    X,
    ChevronLeft,
    ChevronRight,
    Filter,
    FileText,
    ThumbsDown,
    RefreshCw,
    Search,
    Download,
    Loader2,
    AlertTriangle,
    BarChart3,
} from 'lucide-react';
import { Modal } from '../common';
import axiosInstance from '../../utils/axiosConfig';

// ============================================================
// Constantes compartidas de los módulos de Gestión Humana
// (Constancia de Trabajo, Solicitud de Permiso, Solicitud de Vacaciones)
// ============================================================

export const MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export const DIAS_SEMANA = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

// Categorías del catálogo de permisos (Normativa para Control de Asistencia, 2025)
export const CATEGORIAS_PERMISO = [
    { value: 'programado', label: 'Programados, con soporte' },
    { value: 'no_programado', label: 'No programados, a juicio del líder' },
    { value: 'compensatorio', label: 'Descanso compensatorio' },
    { value: 'ley', label: 'De ley' },
    { value: 'otro', label: 'Otro' },
];

export const CATEGORIA_COLORES = {
    programado: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    no_programado: 'bg-amber-50 text-amber-800 border-amber-200',
    compensatorio: 'bg-violet-50 text-violet-800 border-violet-200',
    ley: 'bg-sky-50 text-sky-800 border-sky-200',
    otro: 'bg-gray-100 text-gray-700 border-gray-200',
};

// ============================================================
// Utilidades de fecha
// ============================================================

// 'YYYY-MM-DD' -> 'dd/mm/yyyy'
export const formatFecha = (fecha) => {
    if (!fecha) return '';
    const [y, m, d] = String(fecha).slice(0, 10).split('-');
    if (!y || !m || !d) return fecha;
    return `${d}/${m}/${y}`;
};

// 'YYYY-MM-DD' -> 'lunes 16/11/2026'
export const formatFechaDia = (fecha) => {
    if (!fecha) return '';
    const d = new Date(`${String(fecha).slice(0, 10)}T00:00:00`);
    if (isNaN(d.getTime())) return fecha;
    return `${d.toLocaleDateString('es-VE', { weekday: 'long' })} ${formatFecha(fecha)}`;
};

// '14:30' -> '2:30 p. m.'
export const formatHora = (hora) => {
    if (!hora) return '';
    const [h, m] = hora.split(':').map(Number);
    return `${(h % 12) || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'a. m.' : 'p. m.'}`;
};

// 0.5 -> '1/2 día', 1 -> '1 día', 5 -> '5 días'
export const formatDias = (n) => {
    if (n === null || n === undefined) return '';
    if (n < 1) return '1/2 día';
    return `${n} ${n === 1 ? 'día' : 'días'}`;
};

// ISO datetime -> 'dd de mes de yyyy[, hh:mm]'
export const formatFechaLarga = (iso, withTime = false) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const opts = { day: '2-digit', month: 'long', year: 'numeric' };
    if (withTime) { opts.hour = '2-digit'; opts.minute = '2-digit'; }
    return d.toLocaleDateString('es-VE', opts);
};

export const withAuthToken = (url) => {
    const token = localStorage.getItem('token');
    return token ? `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}` : url;
};

// ============================================================
// EstadoBadge unificado (cubre los estados de los 3 módulos)
// ============================================================

const ESTADO_CONFIG = {
    // Constancia / Permiso
    pendiente: { label: 'Pendiente', bg: 'bg-amber-50 text-amber-600 border-amber-100', icon: Clock },
    aprobada: { label: 'Aprobada', bg: 'bg-emerald-50 text-emerald-600 border-emerald-100', icon: CheckCircle },
    aprobado: { label: 'Aprobado', bg: 'bg-emerald-50 text-emerald-600 border-emerald-100', icon: CheckCircle },
    rechazada: { label: 'Rechazada', bg: 'bg-red-50 text-red-600 border-red-100', icon: XCircle },
    rechazado: { label: 'Rechazado', bg: 'bg-red-50 text-red-600 border-red-100', icon: XCircle },
    cancelado: { label: 'Cancelado', bg: 'bg-gray-50 text-gray-500 border-gray-200', icon: X },
    cancelada: { label: 'Cancelada', bg: 'bg-gray-50 text-gray-500 border-gray-200', icon: X },
    // Vacaciones: solicitada -> aprobada -> registrada
    solicitada: { label: 'Solicitada', bg: 'bg-blue-50 text-blue-600 border-blue-100', icon: Clock },
    registrada: { label: 'Registrada', bg: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle },
};

export const EstadoBadge = ({ estado }) => {
    const c = ESTADO_CONFIG[estado] || ESTADO_CONFIG.pendiente;
    const IconComp = c.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider border ${c.bg}`}>
            <IconComp size={12} />
            {c.label}
        </span>
    );
};

// ============================================================
// Tarjetas / estados vacíos / campos
// ============================================================

export const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex items-center gap-4">
        <div className={`p-4 rounded-2xl ${color}`}>
            <Icon size={24} className="text-white" />
        </div>
        <div>
            <p className="text-sm text-gray-500 font-medium">{title}</p>
            <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
        </div>
    </div>
);

export const EmptyState = ({ icon: Icon = FileText, message, sub }) => (
    <div className="text-center py-16 text-gray-400">
        <Icon size={48} className="mx-auto mb-4 opacity-50" />
        <p className="text-lg font-medium">{message}</p>
        {sub && <p className="text-sm mt-1">{sub}</p>}
    </div>
);

export const InfoField = ({ icon: Icon, label, value, sub }) => (
    <div className="flex items-start gap-3">
        <Icon size={18} className="text-gray-400 mt-0.5 shrink-0" />
        <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider">{label}</p>
            <p className="text-sm font-medium text-gray-900 whitespace-pre-wrap">{value || '-'}</p>
            {sub && <p className="text-xs text-gray-500">{sub}</p>}
        </div>
    </div>
);

export const TabButton = ({ label, tab, active, onClick, badge, icon: Icon, layoutId = 'gh-tab-indicator' }) => (
    <button
        onClick={() => onClick(tab)}
        className={`pb-3 text-sm font-medium transition-colors relative flex items-center gap-2 whitespace-nowrap ${
            active === tab ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'
        }`}
    >
        {Icon && <Icon size={14} />}
        {label}
        {badge > 0 && (
            <span className="bg-cyan-500 text-white text-xs rounded-full min-w-[1.25rem] h-5 px-1 flex items-center justify-center">{badge}</span>
        )}
        {active === tab && <motion.div layoutId={layoutId} className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900 rounded-full" />}
    </button>
);

// ============================================================
// Modal de rechazo (usa el Modal común: Escape, scroll-lock, aria)
// ============================================================

export const RechazoModal = ({ isOpen, onClose, onConfirm, solicitud, motivo, setMotivo, submitting, titulo = 'Rechazar solicitud' }) => (
    <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnOverlay>
        <Modal.Header onClose={onClose}>{titulo}</Modal.Header>
        <Modal.Body>
            {solicitud && (
                <p className="text-sm text-gray-500 mb-4">
                    {solicitud.codigo}
                    {solicitud.empleado_nombre ? ` · ${solicitud.empleado_nombre}` : ''}
                </p>
            )}
            <label htmlFor="gh-motivo-rechazo" className="block text-sm font-medium text-gray-700 mb-2">
                Motivo del rechazo
            </label>
            <textarea
                id="gh-motivo-rechazo"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={4}
                autoFocus
                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none resize-none text-base"
                placeholder="Explica el motivo del rechazo..."
            />
        </Modal.Body>
        <Modal.Footer>
            <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition-colors"
            >
                Cancelar
            </button>
            <button
                type="button"
                onClick={onConfirm}
                disabled={!motivo.trim() || submitting}
                className="px-4 py-2.5 bg-red-500 text-white font-medium rounded-xl hover:bg-red-600 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
                {submitting ? <RefreshCw size={16} className="animate-spin" /> : <ThumbsDown size={16} />}
                Rechazar
            </button>
        </Modal.Footer>
    </Modal>
);

// ============================================================
// Calendario mensual (grilla compartida)
// El caller pasa `eventosPorDia` (mapa dia->eventos), `renderEvento` y `leyenda`.
// ============================================================

export const CalendarioGH = ({
    year,
    month,
    eventosPorDia = {},
    renderEvento,
    leyenda,
    resumen,
    departamentos = [],
    departamentoId,
    onDepartamentoChange,
    onPrev,
    onNext,
    onToday,
    maxPorDia = 3,
}) => {
    const primerDia = new Date(year, month - 1, 1);
    const ultimoDia = new Date(year, month, 0);
    const diasEnMes = ultimoDia.getDate();
    let startDay = primerDia.getDay() - 1;
    if (startDay < 0) startDay = 6;

    const celdas = [];
    for (let i = 0; i < startDay; i++) celdas.push(null);
    for (let d = 1; d <= diasEnMes; d++) celdas.push(d);

    const hoy = new Date();
    const esHoy = (d) => d === hoy.getDate() && month === hoy.getMonth() + 1 && year === hoy.getFullYear();

    return (
        <div className="space-y-4">
            {/* Navegación */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <button onClick={onPrev} className="p-2 hover:bg-gray-100 rounded-xl transition-colors" aria-label="Mes anterior">
                        <ChevronLeft size={18} />
                    </button>
                    <h3 className="text-lg font-bold text-gray-900 min-w-[180px] text-center">
                        {MESES[month - 1]} {year}
                    </h3>
                    <button onClick={onNext} className="p-2 hover:bg-gray-100 rounded-xl transition-colors" aria-label="Mes siguiente">
                        <ChevronRight size={18} />
                    </button>
                    <button onClick={onToday} className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                        Hoy
                    </button>
                </div>
                {onDepartamentoChange && (
                    <div className="flex items-center gap-2">
                        <Filter size={14} className="text-gray-400" />
                        <select
                            value={departamentoId}
                            onChange={(e) => onDepartamentoChange(e.target.value)}
                            aria-label="Filtrar por departamento"
                            className="text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none"
                        >
                            <option value="">Todos los departamentos</option>
                            {departamentos.map((d) => (
                                <option key={d.id} value={d.id}>{d.nombre}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* Leyenda */}
            {leyenda && <div className="flex flex-wrap gap-3">{leyenda}</div>}

            {/* Grilla */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="grid grid-cols-7 border-b border-gray-100">
                    {DIAS_SEMANA.map((d) => (
                        <div key={d} className="py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
                            {d}
                        </div>
                    ))}
                </div>
                <div className="grid grid-cols-7">
                    {celdas.map((dia, idx) => {
                        const eventos = dia ? (eventosPorDia[dia] || []) : [];
                        const finde = idx % 7 === 5 || idx % 7 === 6;
                        return (
                            <div
                                key={idx}
                                className={`min-h-[100px] p-2 border-b border-r border-gray-50 ${dia ? (finde ? 'bg-gray-50/50' : 'bg-white') : 'bg-gray-50/30'}`}
                            >
                                {dia && (
                                    <>
                                        <span className={`text-sm font-medium ${esHoy(dia) ? 'bg-gray-900 text-white w-7 h-7 rounded-full flex items-center justify-center' : 'text-gray-600'}`}>
                                            {dia}
                                        </span>
                                        <div className="mt-1 space-y-1">
                                            {eventos.slice(0, maxPorDia).map((ev, i) => renderEvento(ev, i))}
                                            {eventos.length > maxPorDia && (
                                                <span className="text-[10px] text-gray-400 pl-1">+{eventos.length - maxPorDia} más</span>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {resumen && <div className="text-sm text-gray-500">{resumen}</div>}
        </div>
    );
};

// ============================================================
// Filtros + exportación de paneles GH
// ============================================================

// Descarga un archivo (xlsx/pdf) vía axios (respeta el token del header).
export const descargarBlob = async (url, filename) => {
    const res = await axiosInstance.get(url, { responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
};

// Filtra una lista de solicitudes con accesores por módulo.
// acc: { nombre(it), estado(it), departamento(it)?, fecha(it)? }
export const filtrarGH = (items, { q, estado, departamento, desde, hasta }, acc) => {
    const qn = normalizarTexto(q).trim();
    return items.filter((it) => {
        if (qn && !normalizarTexto(acc.nombre(it)).includes(qn)) return false;
        if (estado && acc.estado(it) !== estado) return false;
        if (departamento && acc.departamento && acc.departamento(it) !== departamento) return false;
        const f = acc.fecha ? acc.fecha(it) : null;
        if (f) {
            const fd = String(f).slice(0, 10);
            if (desde && fd < desde) return false;
            if (hasta && fd > hasta) return false;
        }
        return true;
    });
};

export const FiltrosGH = ({
    q, setQ,
    estado, setEstado, estados,
    departamento, setDepartamento, departamentos,
    desde, setDesde, hasta, setHasta,
    onExport, exporting, total, mostrados,
}) => {
    const hayFiltros = q || estado || departamento || desde || hasta;
    const limpiar = () => { setQ(''); setEstado(''); if (setDepartamento) setDepartamento(''); setDesde(''); setHasta(''); };
    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
            <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[180px]">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Buscar empleado</label>
                    <div className="relative">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text" value={q} onChange={(e) => setQ(e.target.value)}
                            placeholder="Nombre..."
                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none"
                        />
                    </div>
                </div>
                <div className="min-w-[150px]">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Estado</label>
                    <select
                        value={estado} onChange={(e) => setEstado(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none"
                    >
                        <option value="">Todos</option>
                        {estados.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </div>
                {departamentos && setDepartamento && (
                    <div className="min-w-[170px]">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Departamento</label>
                        <select
                            value={departamento} onChange={(e) => setDepartamento(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none"
                        >
                            <option value="">Todos</option>
                            {departamentos.map((d) => <option key={d.id} value={d.nombre}>{d.nombre}</option>)}
                        </select>
                    </div>
                )}
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
                    <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
                        className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
                    <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
                        className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 outline-none" />
                </div>
                <button
                    onClick={onExport} disabled={exporting}
                    className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                    {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                    Exportar Excel
                </button>
            </div>
            <div className="flex items-center justify-between text-xs text-gray-400">
                <span>{mostrados} de {total}</span>
                {hayFiltros && (
                    <button onClick={limpiar} className="flex items-center gap-1 hover:text-gray-600 transition-colors">
                        <X size={12} /> Limpiar filtros
                    </button>
                )}
            </div>
        </div>
    );
};

// ============================================================
// Aviso de solapamiento de fechas (#4) — propio + equipo, no bloquea
// ============================================================

export const AvisoSolapamiento = ({ empleadoId, fechaInicio, fechaFin, excludePermisoId, excludeVacacionId }) => {
    const [data, setData] = React.useState(null);

    React.useEffect(() => {
        if (!empleadoId || !fechaInicio || !fechaFin) {
            setData(null);
            return;
        }
        let activo = true;
        const params = new URLSearchParams({
            empleado_id: empleadoId,
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin,
        });
        if (excludePermisoId) params.append('exclude_permiso_id', excludePermisoId);
        if (excludeVacacionId) params.append('exclude_vacacion_id', excludeVacacionId);
        axiosInstance.get(`/api/gh/solapamiento?${params.toString()}`)
            .then(res => { if (activo) setData(res.data); })
            .catch(() => { if (activo) setData(null); });
        return () => { activo = false; };
    }, [empleadoId, fechaInicio, fechaFin, excludePermisoId, excludeVacacionId]);

    if (!data) return null;
    const propias = data.propias || [];
    const equipoCount = data.equipo_count || 0;
    if (propias.length === 0 && equipoCount === 0) return null;

    return (
        <div className="p-3 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-800 space-y-1">
            <div className="flex items-center gap-2 font-medium">
                <AlertTriangle size={15} /> Posible solapamiento de fechas
            </div>
            {propias.length > 0 && (
                <p>Coincide con {propias.length} solicitud(es) del empleado: {propias.map(p => `${p.detalle} (${p.fechas})`).join('; ')}.</p>
            )}
            {equipoCount > 0 && (
                <p>{equipoCount} compañero(s) del mismo departamento estarán fuera en esas fechas.</p>
            )}
        </div>
    );
};

// ============================================================
// Lo que dicen las reglas sobre lo que se está pidiendo: los errores frenan
// (rojo) y los avisos solo informan (ámbar)
// ============================================================

export const AvisosReglas = ({ errores = [], avisos = [] }) => {
    if (!errores.length && !avisos.length) return null;
    return (
        <div className="space-y-2" role="status">
            {errores.map((t) => (
                <div key={t} className="flex items-start gap-2 px-3 py-2.5 rounded-xl border border-red-200 bg-red-50 text-sm text-red-800">
                    <XCircle size={16} className="mt-0.5 shrink-0" /> <span>{t}</span>
                </div>
            ))}
            {avisos.map((t) => (
                <div key={t} className="flex items-start gap-2 px-3 py-2.5 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-900">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" /> <span>{t}</span>
                </div>
            ))}
        </div>
    );
};

// ============================================================
// Contador informativo de permisos por tipo (#12)
// ============================================================

export const ContadorPermisos = ({ empleadoId }) => {
    const [data, setData] = React.useState(null);

    React.useEffect(() => {
        if (!empleadoId) return;
        let activo = true;
        axiosInstance.get(`/api/permisos/contadores/${empleadoId}`)
            .then(res => { if (activo) setData(res.data); })
            .catch(() => {});
        return () => { activo = false; };
    }, [empleadoId]);

    if (!data || !data.por_tipo || data.por_tipo.length === 0) return null;

    return (
        <div className="p-4 rounded-2xl border border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2 text-xs font-medium text-gray-500 mb-2">
                <BarChart3 size={14} /> Permisos aprobados en {data.year}
            </div>
            <div className="flex flex-wrap items-center gap-2">
                {data.por_tipo.map(t => (
                    <span key={t.tipo} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-white border border-gray-200 text-gray-700">
                        {t.tipo_display}: <span className="font-bold">{t.count}</span>
                        {t.dias > 0 && <span className="text-gray-400">({formatDias(t.dias)})</span>}
                        {t.horas > 0 && <span className="text-gray-400">({t.horas} h)</span>}
                    </span>
                ))}
                <span className="text-xs text-gray-400">Total: {data.total}</span>
            </div>
        </div>
    );
};
